import type { ReactNode } from "react"

/* Paper shapes where content is on its way, each laid out like the thing it
   stands in for. The shapes are hidden from assistive tech. The region says
   what is loading in words, once. */

export function LoadingRegion({
  label,
  className,
  children,
}: {
  label: string
  className?: string
  children: ReactNode
}) {
  return (
    <div role="status" className={className}>
      <span className="visually-hidden">{label}</span>
      <div className="sk-body" aria-hidden="true">
        {children}
      </div>
    </div>
  )
}

function Bar({ className }: { className: string }) {
  return <span className={`skeleton ${className}`} />
}

const range = (count: number) =>
  Array.from({ length: count }, (_, index) => index)

/** The h1 and the lede under it. */
export function HeadingSkeleton() {
  return (
    <div className="sk-heading">
      <Bar className="sk-title" />
      <Bar className="sk-lede" />
    </div>
  )
}

/** A request panel: its head, then rows of tick, name, note and a button. */
export function ChecklistSkeleton({ rows = 3 }: { rows?: number }) {
  return (
    <div className="sk-panel">
      <div className="sk-row sk-row-head">
        <Bar className="sk-line sk-w-30" />
      </div>
      {range(rows).map((index) => (
        <div className="sk-row" key={index}>
          <Bar className="sk-tick" />
          <div className="sk-row-main">
            <Bar className="sk-line" />
            <Bar className="sk-line is-short" />
          </div>
          <Bar className="sk-pill" />
        </div>
      ))}
    </div>
  )
}

/** Rows of file mark, name and size, as a delivery or a history period
 * lists them. */
export function FileRowsSkeleton({ rows = 3 }: { rows?: number }) {
  return (
    <>
      {range(rows).map((index) => (
        <div className="sk-row" key={index}>
          <Bar className="sk-file" />
          <div className="sk-row-main">
            <Bar className="sk-line" />
          </div>
          <Bar className="sk-line sk-size" />
        </div>
      ))}
    </>
  )
}

/** One shared-files card: its title and date, then its files. */
export function DeliverySkeleton() {
  return (
    <div className="sk-panel sk-card">
      <div className="sk-card-head">
        <Bar className="sk-line sk-w-40" />
        <Bar className="sk-line is-short" />
      </div>
      <FileRowsSkeleton />
    </div>
  )
}

/** The thread list beside the new-message form. */
export function ThreadsSkeleton() {
  return (
    <div className="messages-layout">
      <div className="sk-threads">
        {range(3).map((index) => (
          <div className="sk-panel sk-thread" key={index}>
            <div className="sk-row-main">
              <Bar className="sk-line" />
              <Bar className="sk-line is-short" />
            </div>
            <Bar className="sk-line sk-size" />
          </div>
        ))}
      </div>
      <div className="sk-panel sk-form">
        <Bar className="sk-line sk-w-40" />
        <Bar className="sk-field" />
        <Bar className="sk-field is-tall" />
        <Bar className="sk-pill" />
      </div>
    </div>
  )
}

/** Recent activity: a dot, a line and its time. */
export function ActivitySkeleton() {
  return (
    <div className="sk-activity">
      {range(3).map((index) => (
        <div className="sk-event" key={index}>
          <Bar className="sk-dot" />
          <div className="sk-row-main">
            <Bar className="sk-line" />
            <Bar className="sk-line is-short" />
          </div>
        </div>
      ))}
    </div>
  )
}
